import React, { useEffect, useRef, useState } from "react";
import styles from "./ContactComponent.module.css";
import { clean, classifyContact, submitLead } from "../../lib/leads";
import { useT } from "../../i18n";

const PHONE_DISPLAY = "(480) 282-0569";
const PHONE_TEL = "+14802820569";

const Contact = () => {
  const t = useT().start;
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  const [form, setForm] = useState({
    name: "",
    contact: "",
    message: "",
    company: "", // honeypot: real people leave this empty
  });
  const [status, setStatus] = useState("idle"); // idle | invalid | sending | sent | error

  useEffect(() => {
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) {
      setInView(true);
      return;
    }
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setInView(true);
          io.disconnect();
        }
      },
      { threshold: 0.2 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const updateField = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.company) return; // bot filled the honeypot

    const name = clean(form.name);
    const message = clean(form.message);
    const c = classifyContact(form.contact);
    if (!name || !c.ok) {
      setStatus("invalid");
      return;
    }

    setStatus("sending");
    try {
      // Payload shape and endpoint live in lib/leads, shared with the tools'
      // feedback modal so the API only ever sees one contract.
      await submitLead({ name, contact: c, message });
      setStatus("sent");
      setForm({ name: "", contact: "", message: "", company: "" });
    } catch (err) {
      setStatus("error");
    }
  };

  return (
    <section
      id="start"
      ref={ref}
      className={`${styles.start} ${inView ? styles.inview : ""}`}
    >
      <div className={styles.inner}>
        <p className={styles.eyebrow}>{t.eyebrow}</p>
        <h2 className={styles.heading}>{t.heading}</h2>
        <p className={styles.offer}>{t.offer}</p>

        <p className={styles.availability}>
          <svg className={styles.pin} viewBox="0 0 24 24" aria-hidden="true">
            <path d="M12 2C8.1 2 5 5.1 5 9c0 5.2 7 13 7 13s7-7.8 7-13c0-3.9-3.1-7-7-7zm0 9.5a2.5 2.5 0 110-5 2.5 2.5 0 010 5z" />
          </svg>
          {t.availability}
        </p>

        <ol className={styles.steps}>
          {t.steps.map((s) => (
            <li className={styles.step} key={s.title}>
              <h3 className={styles.stepTitle}>{s.title}</h3>
              <p className={styles.stepDetail}>{s.detail}</p>
            </li>
          ))}
        </ol>

        <div className={styles.actions}>
          <a href={`tel:${PHONE_TEL}`} className={styles.callBtn}>
            {t.call} {PHONE_DISPLAY}
          </a>
          <a href={`sms:${PHONE_TEL}`} className={styles.minorBtn}>
            {t.text}
          </a>
        </div>

        <form className={styles.form} onSubmit={handleSubmit} noValidate>
          <p className={styles.formLabel}>{t.formLabel}</p>

          <div className={styles.row}>
            <label className={styles.field}>
              <span>{t.name}</span>
              <input
                type="text"
                name="name"
                value={form.name}
                onChange={updateField}
                autoComplete="name"
                placeholder={t.namePlaceholder}
                required
              />
            </label>
            <label className={styles.field}>
              <span>{t.contact}</span>
              <input
                type="text"
                name="contact"
                value={form.contact}
                onChange={updateField}
                autoComplete="on"
                placeholder={t.contactPlaceholder}
                required
              />
            </label>
          </div>

          <label className={styles.field}>
            <span>{t.message}</span>
            <textarea
              name="message"
              rows="3"
              value={form.message}
              onChange={updateField}
              placeholder={t.messagePlaceholder}
            />
          </label>

          {/* honeypot: hidden from people, tempting to bots */}
          <input
            className={styles.hp}
            type="text"
            name="company"
            tabIndex="-1"
            autoComplete="off"
            value={form.company}
            onChange={updateField}
            aria-hidden="true"
          />

          {status !== "sent" && (
            <button
              type="submit"
              className={styles.submit}
              disabled={status === "sending"}
            >
              {status === "sending" ? t.sending : t.send}
            </button>
          )}

          {status === "invalid" && <p className={styles.formNote}>{t.invalid}</p>}
          {status === "sent" && <p className={styles.formNoteOk}>{t.sent}</p>}
          {status === "error" && (
            <p className={styles.formNote}>
              {t.error}
              {PHONE_DISPLAY}.
            </p>
          )}
        </form>

        <p className={styles.reassure}>{t.reassure}</p>
      </div>
    </section>
  );
};

export default Contact;
